import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { sendChat, getApiKey, logout } from '../lib/api';
import { isProduction } from '../lib/production-guard';
import DefenseIndicator from './DefenseIndicator';
import { Copy, Check, X, Download } from 'lucide-react';

function chatAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey();
  const headers: Record<string, string> = { ...extra };
  if (key) headers['X-API-Key'] = key;
  return headers;
}

function handleChatUnauth(res: Response): boolean {
  if (res.status === 401) { logout(); return true; }
  return false;
}

interface DefenseResult {
  contentScan?: { clean: boolean; findingCount?: number; threatLevel?: string };
  complianceCheck?: { corrected: boolean };
  ipProtection?: { leaked: boolean; termCount?: number };
}

interface PersonaMeta {
  id: string;
  name: string;
  confidence: number;
  trustTier: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  persona?: PersonaMeta;
  model_used?: string;
  defense?: DefenseResult;
}

const TIER_PUBLIC_NAMES: Record<string, string> = {
  G1X: 'Analyst',
  G2X: 'Builder',
  G3X: 'Operator',
  G4X: 'Strategist',
};

const PERSONA_COLORS: Record<string, string> = {
  Analyst: 'bg-blue-600 text-white',
  Builder: 'bg-green-600 text-white',
  Operator: 'bg-amber-600 text-white',
  Strategist: 'bg-purple-600 text-white',
};

const TRUST_COLORS: Record<string, string> = {
  T1: 'text-cyan-400 border-cyan-400',
  T2: 'text-green-400 border-green-400',
  T3: 'text-yellow-400 border-yellow-400',
  T4: 'text-red-400 border-red-400',
};

function getPersonaColor(id: string): string {
  const prefix = id?.substring(0, 3).toUpperCase() || '';
  const publicName = TIER_PUBLIC_NAMES[prefix] || prefix;
  return PERSONA_COLORS[publicName] || 'bg-gray-600 text-white';
}

function getTrustColor(tier: string): string {
  return TRUST_COLORS[tier] || 'text-[#A8A29E] border-gray-400';
}

const TRUST_LABELS: Record<string, string> = {
  T1: 'Standard',
  T2: 'Professional',
  T3: 'Enterprise',
  T4: 'Sovereign',
};

/** Lightweight markdown renderer - returns React elements, no external deps */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      let fenceLine = lines[i];
      while (i < lines.length && fenceLine !== undefined && !fenceLine.startsWith('```')) {
        codeLines.push(fenceLine);
        i++;
        fenceLine = lines[i];
      }
      i++; // skip closing ```
      elements.push(
        <pre key={`code-${i}`} className="bg-gray-950 border border-[#3A352F] rounded px-3 py-2 my-1.5 overflow-x-auto text-xs font-mono">
          {lang && <div className="text-gray-500 text-[10px] mb-1">{lang}</div>}
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Bullet list items
    if (/^[\-\*]\s/.test(line)) {
      const items: string[] = [];
      let bulletLine: string | undefined = line;
      while (i < lines.length && bulletLine !== undefined && /^[\-\*]\s/.test(bulletLine)) {
        items.push(bulletLine.replace(/^[\-\*]\s/, ''));
        i++;
        bulletLine = lines[i];
      }
      elements.push(
        <ul key={`ul-${i}`} className="list-disc list-inside my-1 space-y-0.5">
          {items.map((item, j) => (
            <li key={j} className="text-sm">{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Empty line = paragraph break
    if (line.trim() === '') {
      elements.push(<div key={`br-${i}`} className="h-2" />);
      i++;
      continue;
    }

    // Regular text line
    elements.push(
      <p key={`p-${i}`} className="text-sm">{renderInline(line)}</p>
    );
    i++;
  }

  return <>{elements}</>;
}

/** Renders inline markdown: bold, italic, inline code */
function renderInline(text: string): React.ReactNode {
  // Split on inline patterns: `code`, **bold**, *italic*
  const parts: React.ReactNode[] = [];
  // Combined regex: inline code, bold, italic
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('`')) {
      parts.push(
        <code key={match.index} className="bg-[#3A352F] text-[#D4825F] px-1 py-0.5 rounded text-xs font-mono">
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('**')) {
      parts.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      parts.push(<em key={match.index}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

function makeDemoResponse(userMsg: string): Omit<ChatMessage, 'id' | 'timestamp'> & { conversationId: string } {
  const personas: PersonaMeta[] = [
    { id: 'G1X-ANALYST', name: 'Analyst', confidence: 0.92, trustTier: 'T1' },
    { id: 'G2X-BUILDER', name: 'Builder', confidence: 0.87, trustTier: 'T2' },
    { id: 'G3X-OPERATOR', name: 'Operator', confidence: 0.85, trustTier: 'T2' },
    { id: 'G4X-STRATEGIST', name: 'Strategist', confidence: 0.91, trustTier: 'T1' },
  ];
  const persona = personas[Math.floor(Math.random() * personas.length)];
  if (!persona) {
    // personas is a non-empty literal array; unreachable at runtime, but
    // noUncheckedIndexedAccess means the index read is typed as possibly undefined.
    return {
      role: 'assistant',
      content: 'Processing your request.',
      model_used: 'demo-mode',
      conversationId: 'demo-' + Date.now(),
    };
  }
  const responses: Record<string, string> = {
    'G1X-ANALYST': `Analysis complete. Based on available data, the query "${userMsg.slice(0, 40)}" maps to cognitive pattern C-47. Confidence: high. Recommend deeper module scan.`,
    'G2X-BUILDER': `Build pipeline ready. I can scaffold that component. The current module registry supports this capability natively. Want me to generate the implementation?`,
    'G3X-OPERATOR': `Operational check passed. All systems nominal. The requested action aligns with current access level permissions. Proceeding with standard protocol.`,
    'G4X-STRATEGIST': `Strategic assessment: this aligns with the Q2 roadmap. Risk profile is low. I recommend proceeding with phased rollout using the orchestrator.`,
  };
  return {
    role: 'assistant',
    content: responses[persona.id] || 'Processing your request.',
    persona,
    model_used: 'demo-mode',
    conversationId: 'demo-' + Date.now(),
  };
}

interface ChatSession {
  id: string;
  label: string;
  messages: ChatMessage[];
  conversationId: string | null;
  createdAt: string;
}

const CHAT_API = '/api/chat';

async function fetchSessionList(): Promise<ChatSession[]> {
  try {
    const res = await fetch(`${CHAT_API}/sessions`, { headers: chatAuthHeaders() });
    if (handleChatUnauth(res)) return [];
    if (!res.ok) return [];
    const rows = await res.json();
    return rows.map((r: any) => ({
      id: r.id,
      label: r.label || 'Session',
      messages: [],
      conversationId: null,
      createdAt: r.createdAt,
    }));
  } catch { return []; }
}

async function fetchSessionMessages(sessionId: string): Promise<ChatMessage[]> {
  try {
    const res = await fetch(`${CHAT_API}/sessions/${sessionId}`, { headers: chatAuthHeaders() });
    if (handleChatUnauth(res)) return [];
    if (!res.ok) return [];
    const data = await res.json();
    return (data.messages || []).map((m: any) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp,
      persona: m.persona || undefined,
      model_used: m.model_used || undefined,
      defense: m.defense || undefined,
    }));
  } catch { return []; }
}

async function createSessionOnServer(label?: string): Promise<ChatSession | null> {
  try {
    const res = await fetch(`${CHAT_API}/sessions`, {
      method: 'POST',
      headers: chatAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ label: label || `Session ${new Date().toLocaleTimeString()}` }),
    });
    if (handleChatUnauth(res)) return null;
    if (!res.ok) return null;
    const data = await res.json();
    return { id: data.id, label: data.label, messages: [], conversationId: null, createdAt: data.createdAt };
  } catch { return null; }
}

async function saveMessageToServer(sessionId: string, msg: { role: string; content: string; persona?: any; model_used?: string; defense?: any }) {
  try {
    await fetch(`${CHAT_API}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers: chatAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(msg),
    });
  } catch { /* best-effort persistence */ }
}

async function deleteSessionOnServer(sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`${CHAT_API}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: chatAuthHeaders(),
    });
    if (handleChatUnauth(res)) return false;
    return res.ok;
  } catch { return false; }
}

async function renameSessionOnServer(sessionId: string, label: string): Promise<boolean> {
  try {
    const res = await fetch(`${CHAT_API}/sessions/${sessionId}`, {
      method: 'PUT',
      headers: chatAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ label }),
    });
    if (handleChatUnauth(res)) return false;
    return res.ok;
  } catch { return false; }
}

export function ChatUI({ isDemoMode }: { isDemoMode?: boolean }) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string>('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load sessions from server on mount
  useEffect(() => {
    (async () => {
      const loaded = await fetchSessionList();
      const firstLoaded = loaded[0];
      if (firstLoaded) {
        setSessions(loaded);
        const lastId = firstLoaded.id; // sorted by updated_at DESC
        setActiveSessionId(lastId);
        const msgs = await fetchSessionMessages(lastId);
        setSessions(prev => prev.map(s => s.id === lastId ? { ...s, messages: msgs } : s));
      } else {
        // No sessions yet -- create one
        const newSession = await createSessionOnServer();
        if (newSession) {
          setSessions([newSession]);
          setActiveSessionId(newSession.id);
        }
      }
      setInitializing(false);
    })();
  }, []);

  // Governed voice draft hand-off (VoiceControl): places the confirmed draft
  // text in the input for review. Draft-only by design -- never auto-sends.
  useEffect(() => {
    const onVoiceDraft = (e: Event) => {
      const text = (e as CustomEvent<{ text?: string }>).detail?.text;
      if (typeof text === 'string' && text.trim()) { setInput(text); }
    };
    window.addEventListener('agentomy:voice-draft', onVoiceDraft);
    return () => window.removeEventListener('agentomy:voice-draft', onVoiceDraft);
  }, []);

  const activeSession = sessions.find(s => s.id === activeSessionId) || sessions[0];
  const messages = activeSession?.messages || [];
  const conversationId = activeSession?.conversationId || null;

  const setMessages = (updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    setSessions(prev =>
      prev.map(s =>
        s.id === activeSessionId ? { ...s, messages: updater(s.messages) } : s
      )
    );
  };

  const setConversationId = (cid: string) => {
    setSessions(prev =>
      prev.map(s =>
        s.id === activeSessionId ? { ...s, conversationId: cid } : s
      )
    );
  };

  const handleNewSession = async () => {
    const s = await createSessionOnServer();
    if (s) {
      setSessions(prev => [s, ...prev]);
      setActiveSessionId(s.id);
    }
  };

  const handleExportPDF = () => {
    if (messages.length === 0) return;
    const sessionLabel = activeSession?.label || 'Agentomy Chat';
    const exportDate = new Date().toLocaleString();

    const messagesHtml = messages.map(msg => {
      const isUser = msg.role === 'user';
      const label = isUser ? 'You' : (msg.persona?.name || msg.role);
      const time = new Date(msg.timestamp).toLocaleTimeString();
      const model = msg.model_used ? ` &middot; via ${msg.model_used}` : '';
      const bgColor = isUser ? '#155e75' : '#1f2937';
      const content = msg.content
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return `
        <div style="margin-bottom:16px;">
          <div style="font-size:12px;color:#9ca3af;margin-bottom:4px;">
            <strong style="color:${isUser ? '#22d3ee' : '#34d399'}">${label}</strong>
            &middot; ${time}${model}
          </div>
          <div style="background:${bgColor};padding:12px 16px;border-radius:8px;color:#F5F0E8;font-size:14px;line-height:1.6;white-space:pre-wrap;">
            ${content}
          </div>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${sessionLabel} - Agentomy Chat Export</title>
<style>
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #1C1917; color: #F5F0E8; max-width: 800px; margin: 0 auto; padding: 40px 20px; }
  h1 { font-size: 20px; color: #22d3ee; border-bottom: 1px solid #30363d; padding-bottom: 12px; }
  .meta { font-size: 12px; color: #A8A29E; margin-bottom: 24px; }
</style>
</head><body>
<h1>${sessionLabel}</h1>
<div class="meta">Exported ${exportDate} &middot; ${messages.length} messages &middot; Agentomy Governance Platform</div>
${messagesHtml}
</body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  const handleSwitchSession = async (sid: string) => {
    setActiveSessionId(sid);
    // Load messages if not already loaded
    const existing = sessions.find(s => s.id === sid);
    if (existing && existing.messages.length === 0) {
      const msgs = await fetchSessionMessages(sid);
      setSessions(prev => prev.map(s => s.id === sid ? { ...s, messages: msgs } : s));
    }
  };

  const handleCopyMessage = useCallback(async (msgId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMsgId(msgId);
      setTimeout(() => setCopiedMsgId(null), 1500);
    } catch { /* clipboard not available */ }
  }, []);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    if (sessions.length <= 1) return; // don't delete the last session
    const ok = await deleteSessionOnServer(sessionId);
    if (ok) {
      setSessions(prev => {
        const next = prev.filter(s => s.id !== sessionId);
        const nextFirst = next[0];
        if (sessionId === activeSessionId && nextFirst) {
          setActiveSessionId(nextFirst.id);
        }
        return next;
      });
    }
  }, [sessions.length, activeSessionId]);

  const handleStartRename = useCallback((sessionId: string, currentLabel: string) => {
    setEditingSessionId(sessionId);
    setEditLabel(currentLabel);
  }, []);

  const handleFinishRename = useCallback(async () => {
    if (!editingSessionId || !editLabel.trim()) {
      setEditingSessionId(null);
      return;
    }
    const ok = await renameSessionOnServer(editingSessionId, editLabel.trim());
    if (ok) {
      setSessions(prev => prev.map(s =>
        s.id === editingSessionId ? { ...s, label: editLabel.trim() } : s
      ));
    }
    setEditingSessionId(null);
  }, [editingSessionId, editLabel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Persist user message to server
    if (activeSessionId) {
      saveMessageToServer(activeSessionId, { role: 'user', content: text });
    }

    const apiMessages = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));

    let result = null;
    if (!isDemoMode) {
      result = await sendChat(apiMessages, conversationId ? { conversationId } : {});
    }

    if (!result) {
      if (isProduction()) {
        // Production: show error, no mock response
        const errorMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Chat is unavailable. The most common cause is that no AI model provider key is configured on the server -- Agentomy is bring-your-own-key. Set a provider key (for example OPENROUTER_API_KEY or ANTHROPIC_API_KEY) in the server environment and restart. If a key is already configured, the model provider may be temporarily unreachable.',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMsg]);
        if (activeSessionId) {
          saveMessageToServer(activeSessionId, { role: 'assistant', content: errorMsg.content });
        }
      } else {
        // Dev fallback: demo response
        const demo = makeDemoResponse(text);
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: demo.content,
          timestamp: new Date().toISOString(),
          ...(demo.persona !== undefined ? { persona: demo.persona } : {}),
          ...(demo.model_used !== undefined ? { model_used: demo.model_used } : {}),
        };
        if (!conversationId) setConversationId(demo.conversationId);
        setMessages(prev => [...prev, assistantMsg]);
        if (activeSessionId) {
          saveMessageToServer(activeSessionId, {
            role: 'assistant', content: demo.content,
            ...(demo.persona !== undefined ? { persona: demo.persona } : {}),
            ...(demo.model_used !== undefined ? { model_used: demo.model_used } : {}),
          });
        }
      }
    } else {
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: result.data?.choices?.[0]?.message?.content || result.data?.content || result.response || result.content || 'No response content.',
        timestamp: new Date().toISOString(),
        persona: result.agent?.role || result.persona || undefined,
        model_used: result.model || result.model_used || undefined,
        defense: result.defense || undefined,
      };
      if (result.conversationId) setConversationId(result.conversationId);
      setMessages(prev => [...prev, assistantMsg]);
      if (activeSessionId) {
        saveMessageToServer(activeSessionId, {
          role: 'assistant', content: assistantMsg.content,
          ...(assistantMsg.persona !== undefined ? { persona: assistantMsg.persona } : {}),
          ...(assistantMsg.model_used !== undefined ? { model_used: assistantMsg.model_used } : {}),
          ...(assistantMsg.defense !== undefined ? { defense: assistantMsg.defense } : {}),
        });
      }
    }

    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (initializing) {
    return (
      <Card className="bg-gray-900 border-[#3A352F] flex flex-col h-[600px]">
        <CardContent className="flex items-center justify-center h-full text-gray-500 text-sm">
          Loading sessions...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 border-[#3A352F] flex flex-col h-[600px]">
      <CardContent className="flex flex-col h-full p-4 gap-4">
        {/* Header with session tabs */}
        <div className="border-b border-[#3A352F] pb-3 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-100">Agentomy Chat</h2>
            <div className="flex items-center gap-2">
              {isDemoMode && (
                <Badge variant="outline" className="text-orange-400 border-orange-400 text-xs">DEMO</Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-[#4A443D] text-[#A8A29E] hover:text-white hover:bg-[#292524]"
                onClick={handleExportPDF}
                disabled={messages.length === 0}
                title="Export chat as PDF"
              >
                <Download className="w-3 h-3 mr-1" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-[#4A443D] text-[#A8A29E] hover:text-white hover:bg-[#292524]"
                onClick={handleNewSession}
              >
                + New Session
              </Button>
            </div>
          </div>
          {sessions.length > 1 && (
            <div className="flex gap-1 overflow-x-auto">
              {sessions.map((s, i) => {
                const firstMsg = s.messages[0];
                return (
                <div key={s.id} className="flex items-center group/tab">
                  {editingSessionId === s.id ? (
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                      onBlur={handleFinishRename}
                      onKeyDown={e => { if (e.key === 'Enter') handleFinishRename(); if (e.key === 'Escape') setEditingSessionId(null); }}
                      className="px-2 py-1 text-xs rounded-md bg-[#3A352F] text-white border border-[#C2714F] outline-none w-32"
                    />
                  ) : (
                    <button
                      onClick={() => handleSwitchSession(s.id)}
                      onDoubleClick={() => {
                        const label = s.label !== 'Session'
                          ? s.label
                          : firstMsg
                            ? firstMsg.content.slice(0, 24)
                            : `Session ${i + 1}`;
                        handleStartRename(s.id, label);
                      }}
                      className={`px-3 py-1 text-xs rounded-l-md whitespace-nowrap transition-colors ${
                        s.id === activeSessionId
                          ? 'bg-[#C2714F] text-white'
                          : 'bg-[#292524] text-[#A8A29E] hover:bg-[#3A352F] hover:text-gray-200'
                      }`}
                    >
                      {s.label !== 'Session'
                        ? s.label.slice(0, 24) + (s.label.length > 24 ? '...' : '')
                        : firstMsg
                          ? firstMsg.content.slice(0, 24) + (firstMsg.content.length > 24 ? '...' : '')
                          : `Session ${i + 1}`
                      }
                      <span className="ml-1 text-gray-500">({s.messages.length})</span>
                    </button>
                  )}
                  {sessions.length > 1 && editingSessionId !== s.id && (
                    <button
                      onClick={e => { e.stopPropagation(); handleDeleteSession(s.id); }}
                      className={`px-1 py-1 text-xs rounded-r-md transition-colors opacity-0 group-hover/tab:opacity-100 ${
                        s.id === activeSessionId
                          ? 'bg-[#7A3E28] text-[#F5F0E8] hover:bg-red-700 hover:text-white'
                          : 'bg-[#292524] text-gray-500 hover:bg-red-700 hover:text-white'
                      }`}
                      title="Delete session"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Send a message to begin. The agent router will select the right agent for your query.
            </div>
          )}
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group/msg`}>
              <div className={`max-w-[75%] rounded-lg px-4 py-2.5 relative ${
                msg.role === 'user'
                  ? 'bg-[#C2714F] text-white'
                  : 'bg-[#292524] text-gray-100 border border-[#3A352F]'
              }`}>
                {/* Persona badges for assistant */}
                {msg.role === 'assistant' && msg.persona && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <Badge className={`text-xs ${getPersonaColor(msg.persona.id)}`}>
                      {msg.persona.name}
                    </Badge>
                    <Badge variant="outline" className={`text-xs ${getTrustColor(msg.persona.trustTier)}`}>
                      {TRUST_LABELS[msg.persona.trustTier] || msg.persona.trustTier}
                    </Badge>
                    <span className="text-xs text-gray-500">{Math.round(msg.persona.confidence * 100)}%</span>
                  </div>
                )}
                {/* Markdown-rendered content */}
                <div className="text-sm">{renderMarkdown(msg.content)}</div>
                {/* Copy button for assistant messages */}
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => handleCopyMessage(msg.id, msg.content)}
                    className="absolute top-2 right-2 p-1 rounded text-gray-500 hover:text-white hover:bg-[#3A352F] opacity-0 group-hover/msg:opacity-100 transition-opacity"
                    title="Copy message"
                  >
                    {copiedMsgId === msg.id
                      ? <Check className="w-3.5 h-3.5 text-green-400" />
                      : <Copy className="w-3.5 h-3.5" />
                    }
                  </button>
                )}
                {/* Defense indicator for assistant messages */}
                {msg.role === 'assistant' && (
                  <DefenseIndicator
                    {...(msg.defense !== undefined ? { defense: msg.defense } : {})}
                    compact={msg.defense?.contentScan?.clean !== false}
                  />
                )}
                {/* Footer: model + time */}
                <div className="flex items-center justify-between mt-1.5 gap-3">
                  {msg.model_used && (
                    <span className="text-xs text-gray-500">via {msg.model_used}</span>
                  )}
                  <span className="text-xs text-[#8F857B]">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#292524] border border-[#3A352F] rounded-lg px-4 py-2.5">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[#D4825F] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-[#D4825F] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-[#D4825F] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="flex gap-2 border-t border-[#3A352F] pt-3">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="bg-[#292524] border-[#4A443D] text-gray-100 placeholder:text-gray-500"
            disabled={loading}
          />
          <Button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="bg-[#C2714F] hover:bg-[#D4825F] text-white px-6"
          >
            Send
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ChatUI;
